// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { FORMID, TestRunParams } from "../TestRun/TestRunConstants";
import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import React, { memo, useCallback, useEffect } from "react";
import { updateUserInterpretation, useTestRunState } from "store/testrun";
import { useHistory, useParams } from "react-router-dom";

import QuestionnaireForm from "../ui/Questionnaire/Questionnaire";
import QuestionnaireInput from "components/ui/Questionnaire/QuestionnaireInput";
import { confirmAlert } from "../../utils/confirmAlert";
import { setStepReady } from "components/TestRun/TestRunStepReadyEmitter";
import { useAppDispatch } from "store";
import { useAuthState } from "../../store/auth";
import { useTestRunConfig } from "components/TestRun/useTestRunConfig";
import { useTheme } from "@material-ui/core";

const pinkLineOptions = [
  {
    value: "noPink",
    name: "Blue line only",
    asset: {
      height: 180,
      width: 290,
      alt: "Test strip with only a blue line.",
      src: "blueonly.png",
    },
  },
  {
    value: "yesAboveBlue",
    name: "Pink line above the blue line",
    asset: {
      height: 180,
      width: 290,
      alt: "Test strip with a pink line above the blue line.",
      src: "pinklineaboveblueline.png",
    },
  },
  {
    value: "yesBelowBlue",
    name: "Pink line below the blue line",
    asset: {
      height: 180,
      width: 290,
      alt: "Test strip with a pink line below the blue line.",
      src: "pinklinebelowblueline.png",
    },
  },
  {
    value: "yesAboveBelowBlue",
    name: "Pink line above and below the blue line",
    asset: {
      height: 180,
      width: 290,
      alt: "Test strip with a pink line above and below the blue line.",
      src: "pinklineabovebelow.png",
    },
  },
];

const questionValidator = (text: string | null) => {
  if (!text || text === "") {
    return "Must enter a value";
  }

  return pinkLineOptions.some(option => text === option.value)
    ? undefined
    : "Select an option";
};

const WhatDoYouSee2Panel = memo(() => {
  // TODO: get questionaire based on the type of strip selected
  const { step, testRunUID } = useParams<TestRunParams>();
  const { getNextDefaultStep } = useTestRunConfig();
  const QuestionnaireFormMemoized = memo(QuestionnaireForm);
  const history = useHistory();
  const { auth } = useAuthState();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const testRunState = useTestRunState();

  const stepReadyCallback = useCallback(
    (stepReady: boolean) => {
      setStepReady(step, stepReady);
    },
    [step]
  );

  const previousResult =
    testRunState?.testRun?.userInterpretation?.pinkLine || "";

  // If already recorded, enable "next" button
  useEffect(() => {
    if (previousResult) {
      stepReadyCallback(true);
    }
  }, [previousResult, stepReadyCallback]);

  // TODO: Generic Error handler.
  if (!auth) {
    return <div>Oops, something went wrong</div>;
  }

  if (!testRunState.subscribed) {
    return <div />; // Add Loader
  }

  return (
    <QuestionnaireFormMemoized
      formId={FORMID}
      onFormReady={stepReadyCallback}
      onSubmit={async (event, questions) => {
        try {
          event.preventDefault();

          await dispatch(
            updateUserInterpretation({
              userInterpretation: {
                pinkLine: questions["pinkLine"].value,
              },
            })
          );

          // Resets the state.
          stepReadyCallback(false);
          const nextStep = getNextDefaultStep({
            currentStepName: step!,
          });
          const path = getPath(ROUTE_DEFINITIONS.PERFORMTEST, {
            params: {
              ":testRunUID": testRunUID,
              ":step": nextStep!,
            },
          });
          history.push(path);
        } catch (e) {
          confirmAlert({ title: "oops", message: e.message, theme });
        }
      }}
    >
      <QuestionnaireInput
        label="Which image most closely matches your test strip?"
        mandatory={true}
        name="pinkLine"
        placeholder="Choose an option"
        type="radio"
        validators={[questionValidator]}
        selectOptions={pinkLineOptions}
        value={previousResult}
      />
    </QuestionnaireFormMemoized>
  );
});

export default WhatDoYouSee2Panel;
